import { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, UserPlus, Mail, Clock, SkipForward, AlertTriangle, MessageCircle,
  XCircle, Eye, MousePointer, Check, CheckCheck, CreditCard, RefreshCw, Ban,
  ShieldOff, Circle, Megaphone,
} from "lucide-react";

const ICON_MAP = {
  "user-plus": UserPlus, "mail": Mail, "clock": Clock, "skip-forward": SkipForward,
  "alert-triangle": AlertTriangle, "message-circle": MessageCircle, "x-circle": XCircle,
  "eye": Eye, "mouse-pointer": MousePointer, "check": Check, "check-check": CheckCheck,
  "credit-card": CreditCard, "refresh-cw": RefreshCw, "ban": Ban,
  "shield-off": ShieldOff, "circle": Circle, "megaphone": Megaphone,
};

const TYPE_COLOR = {
  signup: "bg-blue-50 border-blue-200 text-blue-800",
  email_scheduled: "bg-slate-50 border-slate-200 text-slate-600",
  email_sent: "bg-emerald-50 border-emerald-200 text-emerald-800",
  email_skipped: "bg-amber-50 border-amber-200 text-amber-800",
  email_failed: "bg-red-50 border-red-200 text-red-800",
  email_opened: "bg-teal-50 border-teal-200 text-teal-800",
  email_clicked: "bg-indigo-50 border-indigo-200 text-indigo-800",
  wa_enrolled: "bg-blue-50 border-blue-200 text-blue-800",
  wa_sent: "bg-green-50 border-green-200 text-green-800",
  wa_failed: "bg-red-50 border-red-200 text-red-800",
  wa_read: "bg-teal-50 border-teal-200 text-teal-800",
  wa_unenrolled: "bg-slate-50 border-slate-200 text-slate-600",
  broadcast_email_sent: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800",
  broadcast_email_failed: "bg-red-50 border-red-200 text-red-800",
  broadcast_email_pending: "bg-slate-50 border-slate-200 text-slate-600",
  broadcast_email_skipped_purchased: "bg-emerald-50 border-emerald-200 text-emerald-800",
  broadcast_email_skipped_suppressed: "bg-amber-50 border-amber-200 text-amber-800",
  broadcast_wa_sent: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800",
  broadcast_wa_failed: "bg-red-50 border-red-200 text-red-800",
  broadcast_wa_pending: "bg-slate-50 border-slate-200 text-slate-600",
  broadcast_wa_cancelled: "bg-slate-50 border-slate-200 text-slate-500",
  purchase: "bg-primary/10 border-primary/30 text-primary",
  renewal: "bg-emerald-50 border-emerald-200 text-emerald-800",
  subscription_canceled: "bg-red-50 border-red-200 text-red-800",
  unsubscribed: "bg-red-50 border-red-200 text-red-800",
  suppressed: "bg-red-50 border-red-200 text-red-800",
  wa_suppressed: "bg-red-50 border-red-200 text-red-800",
  delivered: "bg-teal-50 border-teal-200 text-teal-700",
};

export default function AdminJourney() {
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | email | whatsapp | purchase | reply

  const search = async (e) => {
    e && e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setBusy(true); setError(""); setData(null);
    try {
      const isPhone = /^[\d+\s\-()]+$/.test(q);
      const params = isPhone ? { phone: q } : { email: q };
      const { data } = await api.get("/admin/journey", { params });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "لم يتم العثور على هذا العميل");
    } finally {
      setBusy(false);
    }
  };

  const filtered = !data ? [] : data.timeline.filter((e) => {
    if (filter === "all") return true;
    if (filter === "automation") return e.type.startsWith("email_") || e.type.startsWith("wa_") || e.type === "signup";
    if (filter === "broadcast") return e.type.startsWith("broadcast_");
    if (filter === "email") return e.type.startsWith("email_") || e.type === "broadcast_email_sent" || e.type === "broadcast_email_failed";
    if (filter === "whatsapp") return e.type.startsWith("wa_") || e.type.startsWith("broadcast_wa_");
    if (filter === "purchase") return ["purchase", "renewal", "subscription_canceled"].includes(e.type);
    return true;
  });

  return (
    <div className="space-y-4" dir="rtl" data-testid="admin-journey">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold">رحلة العميل</h1>
        <p className="text-foreground/60 mt-1 text-sm">
          ابحث بالبريد أو رقم WhatsApp لترى كل رسالة أُرسلت له، وكل نقرة، وكل عملية شراء — بترتيب زمني.
        </p>
      </div>

      <form onSubmit={search} className="flex gap-2 max-w-xl">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="مثال: customer@example.com أو +9665..."
          className="bg-white border-foreground/10 text-foreground"
          data-testid="journey-search-input"
        />
        <Button type="submit" disabled={busy} data-testid="journey-search-btn">
          <Search className="h-4 w-4 me-1" />{busy ? "..." : "بحث"}
        </Button>
      </form>

      {error && (
        <Card className="p-4 bg-red-50 border-red-200 text-red-800 text-sm" data-testid="journey-error">
          {error}
        </Card>
      )}

      {data && (
        <>
          {/* Contact header */}
          <Card className="p-4 border-foreground/10" data-testid="journey-contact-card">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-semibold text-lg">{data.contact.full_name || "(بدون اسم)"}</h2>
                <p className="text-xs text-foreground/60 mt-1 font-mono">
                  {data.contact.email && <span>{data.contact.email}</span>}
                  {data.contact.email && data.contact.phone && <span className="mx-2">·</span>}
                  {data.contact.phone && <span dir="ltr">{data.contact.phone}</span>}
                </p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {data.contact.purchased && (
                    <span className="text-[11px] bg-emerald-100 text-emerald-800 rounded px-2 py-0.5">✓ اشترى</span>
                  )}
                  {!data.contact.purchased && data.contact.lead_source && (
                    <span className="text-[11px] bg-blue-100 text-blue-800 rounded px-2 py-0.5">
                      Lead ({data.contact.lead_source})
                    </span>
                  )}
                  {data.contact.user_access_type === "free_forever" && (
                    <span className="text-[11px] bg-purple-100 text-purple-800 rounded px-2 py-0.5">
                      وصول مجاني دائم
                    </span>
                  )}
                  {data.active_sequences.map((s, i) => (
                    <span key={i} className="text-[11px] bg-amber-100 text-amber-800 rounded px-2 py-0.5">
                      داخل: {s.name} ({s.channel === "email" ? "بريد" : "واتساب"})
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-center text-xs">
                <MiniStat n={data.stats.emails_sent} label="بريد تلقائي" />
                <MiniStat n={data.stats.wa_sent} label="واتساب تلقائي" />
                <MiniStat n={data.stats.broadcasts_email + data.stats.broadcasts_wa} label="حملات مُرسلة" />
                <MiniStat n={data.stats.purchases} label="شراء" />
              </div>
            </div>
          </Card>

          {/* Filter chips */}
          <div className="flex gap-1 flex-wrap">
            {[
              { k: "all", l: "الكل" },
              { k: "automation", l: "التسلسلات (تلقائي)" },
              { k: "broadcast", l: "الحملات" },
              { k: "email", l: "البريد" },
              { k: "whatsapp", l: "WhatsApp" },
              { k: "purchase", l: "الشراء" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1 rounded text-xs border ${filter === f.k ? "bg-black text-white border-foreground" : "bg-white border-foreground/10"}`}
                data-testid={`journey-filter-${f.k}`}
              >{f.l}</button>
            ))}
          </div>

          {/* Timeline */}
          <Card className="p-4 border-foreground/10">
            {filtered.length === 0 ? (
              <p className="text-xs text-foreground/50 text-center py-8">لا أحداث</p>
            ) : (
              <ol className="relative border-s-2 border-foreground/10 ms-3 space-y-3">
                {filtered.map((e, i) => {
                  const Icon = ICON_MAP[e.icon] || Circle;
                  const cls = TYPE_COLOR[e.type] || "bg-slate-50 border-slate-200 text-slate-700";
                  return (
                    <li key={i} className="ms-4" data-testid={`journey-event-${e.type}-${i}`}>
                      <span className={`absolute -start-3 flex h-6 w-6 items-center justify-center rounded-full border ${cls}`}>
                        <Icon className="h-3 w-3" />
                      </span>
                      <div className={`rounded-md border p-2 ${cls} inline-block max-w-full`}>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-medium text-sm">{e.title}</span>
                          {e.status && <span className="text-[10px] opacity-80">· {e.status}</span>}
                          {e.step && <span className="text-[10px] opacity-70">· {e.step}</span>}
                          {e.campaign && <span className="text-[10px] opacity-70">· {e.campaign}</span>}
                          {e.coupon_code && (
                            <span className="text-[10px] font-mono bg-white/60 rounded px-1">🎟 {e.coupon_code}</span>
                          )}
                        </div>
                        {e.snippet && (
                          <div className="text-xs opacity-80 mt-1 truncate max-w-lg">{e.snippet}</div>
                        )}
                        {e.detail && <div className="text-[10px] opacity-70 mt-0.5">{e.detail}</div>}
                        {e.amount && (
                          <div className="text-[11px] opacity-80 mt-0.5">
                            {e.amount} {e.plan ? `· ${e.plan}` : ""}
                          </div>
                        )}
                        <div className="text-[10px] opacity-60 mt-1">{new Date(e.at).toLocaleString("ar-SA")}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function MiniStat({ n, label }) {
  return (
    <div>
      <div className="text-lg font-bold">{n}</div>
      <div className="text-[10px] text-foreground/50">{label}</div>
    </div>
  );
}
